import {
  Children,
  forwardRef,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';

import { useBehavioralTrackingProps } from '../../../../core/hooks/useBehavioralTrackingProps.js';
import {
  Intent,
  type IntentProps,
} from '../../../../core/slots/Intent/Intent.js';
import type { BehaviorTrackingProps } from '../../../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../../../core/types/data-props.js';
import type { StylingProps } from '../../../../core/types/styling-props.js';
import type { WithChildren } from '../../../../core/types/with-children.js';
import { isSlot } from '../../../../core/utils/is-slot.js';
import { devLog } from '../../../../core/utils/logging.js';
import { Flex } from '../../../../layouts/flex/Flex.js';
import type { DataTablePinnedColumnState } from '../../features/ColumnPinning/column-pinning-types.js';
import type {
  DataTableColumnVisibilityState,
  DataTableVisibleColumnLimits,
} from '../../features/ColumnVisibility/column-visibility-types.js';
import type { DataTableDownloadHeaderContext } from '../../features/Download/download-types.js';
import { DataTableDownload } from '../../features/Download/Download.js';
import { DataTableGlobalLineWrapAction } from '../../features/LineWrap/LineWrap.js';
import { DataTableGlobalLineWrapToolbarAction } from '../../features/LineWrap/LineWrapToolbarAction.js';
import type { DataTableColumnSettingsTriggerProps } from './ColumnSettings/column-settings-types.js';
import { DataTableColumnOrderSettings } from './ColumnSettings/DataTableColumnOrderSettings.js';
import { DataTableColumnPinningSettings } from './ColumnSettings/DataTableColumnPinningSettings.js';
import { DataTableColumnSettings } from './ColumnSettings/DataTableColumnSettings.js';
import { DataTableColumnSettingsTrigger } from './ColumnSettings/DataTableColumnSettingsTrigger.js';
import { DataTableColumnVisibilitySettings } from './ColumnSettings/DataTableColumnVisibilitySettings.js';
import { DataTableToolbarMenu } from './DataTableToolbarMenu.js';

/**
 * @public
 */
export type DataTableToolbarProps = WithChildren &
  StylingProps &
  DataTestId &
  BehaviorTrackingProps;

/**
 * A helper function to check whether the child component is the DataTableToolbar component.
 * @internal
 */
export function _isDataTableToolbar(
  child: ReactElement | null,
): child is ReactElement<DataTableToolbarProps> {
  return child?.type === DataTableToolbar;
}

/**
 * Allowed slots for the DataTableToolbar.
 * @internal
 */
export type DataTableToolbarSlots = {
  visibilitySettings: boolean;
  columnOrderSettings: boolean;
  columnPinningSettings: boolean;
  downloadData: boolean;
  globalLineWrap: boolean;
};

/**
 * Props for the DataTableToolbar items.
 * @internal
 */
export type DataTableToolbarItemProps = {
  /**
   * Column visibility reset state that will be used to reset and also to enable/disable 'Reset to default' in column settings modal.
   */
  resetColumnVisibility?: DataTableColumnVisibilityState;
  /**
   * Upper limits for the number of visible columns in the column settings modal.
   */
  visibleColumnLimits?: DataTableVisibleColumnLimits;
  /**
   * Column order reset state that will be used to reset and also to enable/disable 'Reset to default' in column settings modal.
   */
  resetColumnOrder?: string[];
  /**
   * Column pinning reset state that will be used to reset and also to enable/disable 'Reset to default' in column settings modal.
   */
  resetColumnPinning?: DataTablePinnedColumnState;
  /**
   * The remaining props for the ColumnSettingsTrigger.
   */
  customTriggerProps?: DataTableColumnSettingsTriggerProps;
  /**
   * Array of column IDs that can optionally be excluded from the download.
   */
  downloadExcludedColumns?: string[];
  /**
   * Callback, that fires once data has been downloaded and indicates which subset of data was downloaded (all, page or selected) and which columns were excluded.
   */
  onDownloadData?: (
    subset: 'all' | 'page' | 'selected',
    excludedColumns?: string[],
  ) => void;
  /**
   * Callback to override the header name of each included leaf column in the downloaded file.
   */
  formatDownloadHeader?: (
    context: DataTableDownloadHeaderContext,
  ) => string | undefined;
  /**
   * Name of the downloaded CSV file.
   */
  fileName?: string | ((subset: 'all' | 'page' | 'selected') => string);
  /**
   * Array of intent props for the `Intent` slot, to render the intent actions in the menu.
   */
  intents: Array<IntentProps>;
};

/**
 * @internal
 */
export function getToolbarConfig(children: ReactNode) {
  const slots: DataTableToolbarSlots = {
    visibilitySettings: false,
    columnOrderSettings: false,
    columnPinningSettings: false,
    downloadData: false,
    globalLineWrap: false,
  };
  const toolbarItemProps: DataTableToolbarItemProps = { intents: [] };

  Children.forEach(children, (child) => {
    if (isSlot(child, DataTableColumnVisibilitySettings)) {
      slots.visibilitySettings = true;
      toolbarItemProps.resetColumnVisibility =
        child.props?.resetColumnVisibility;
      toolbarItemProps.visibleColumnLimits = child.props?.visibleColumnLimits;
    } else if (isSlot(child, DataTableColumnOrderSettings)) {
      slots.columnOrderSettings = true;
      toolbarItemProps.resetColumnOrder = child.props?.resetColumnOrder;
    } else if (isSlot(child, DataTableColumnPinningSettings)) {
      slots.columnPinningSettings = true;
      toolbarItemProps.resetColumnPinning = child.props?.resetColumnPinning;
    } else if (isSlot(child, DataTableColumnSettingsTrigger)) {
      toolbarItemProps.customTriggerProps = child.props;
    } else if (isSlot(child, DataTableDownload)) {
      slots.downloadData = true;
      toolbarItemProps.downloadExcludedColumns = child.props?.excludeColumns;
      toolbarItemProps.onDownloadData = child.props?.onDownloadData;
      toolbarItemProps.formatDownloadHeader = child.props?.formatDownloadHeader;
      toolbarItemProps.fileName = child.props?.fileName;
    } else if (isSlot(child, DataTableGlobalLineWrapAction)) {
      slots.globalLineWrap = true;
    } else if (isSlot(child, Intent)) {
      toolbarItemProps.intents.push(child.props);
    } else {
      devLog('Invalid child of DataTableToolbar');
    }
  });

  return { slots, toolbarItemProps };
}

/**
 * Toolbar slot component for general actions that affect the entire table. Positioned above the header.
 * @public
 */
export const DataTableToolbar = /* @__PURE__ */ forwardRef(
  (
    props: DataTableToolbarProps,
    forwardedRef: Ref<HTMLDivElement>,
  ): ReactElement | null => {
    const {
      children,
      'data-testid': dataTestId,
      className: consumerClassName,
      style: consumerStyle,
      ...remainingProps
    } = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);

    const { slots, toolbarItemProps } = getToolbarConfig(children);

    if (Children.count(children) === 0) {
      return null;
    }

    return (
      <Flex
        ref={forwardedRef}
        justifyContent="flex-end"
        alignItems="flex-end"
        gap={4}
        data-testid={dataTestId}
        className={consumerClassName}
        style={{
          gridArea: '1 / 2 / 2 / 2',
          ...consumerStyle,
        }}
        {...behavioralTrackingProps}
        {...restProps}
      >
        {(slots.visibilitySettings ||
          slots.columnOrderSettings ||
          slots.columnPinningSettings) && (
          <DataTableColumnSettings
            customTriggerProps={toolbarItemProps?.customTriggerProps}
          />
        )}
        {slots.globalLineWrap && <DataTableGlobalLineWrapToolbarAction />}
        {(slots.downloadData || toolbarItemProps.intents.length > 0) && (
          <DataTableToolbarMenu
            hasDownloadData={slots.downloadData}
            downloadExcludedColumns={toolbarItemProps?.downloadExcludedColumns}
            fileName={toolbarItemProps?.fileName}
            intents={toolbarItemProps.intents}
          />
        )}
      </Flex>
    );
  },
);

(
  DataTableToolbar as typeof DataTableToolbar & {
    displayName: string;
  }
).displayName = 'DataTable.Toolbar';
